/**
 * Blog content — the starter set of articles served at /blog.
 *
 * Articles are stored as structured blocks rather than raw HTML or
 * markdown, so they render natively in the dark theme with no extra
 * rendering dependency. Listed newest-first; the first entry is the
 * featured post on the index.
 */

export type BlogBlock =
  | { type: 'p'; text: string }
  | { type: 'h2'; text: string }
  | { type: 'quote'; text: string; translation: string; source: string };

export type BlogPost = {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  author: string;
  /** ISO date, YYYY-MM-DD. */
  date: string;
  readingMinutes: number;
  body: BlogBlock[];
};

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: 'building-a-daily-habit',
    title: 'Building a daily habit with the Quran',
    excerpt:
      'Consistency beats intensity. A few honest minutes every day will carry you further than an ambitious plan you cannot keep.',
    category: 'Practice',
    author: 'The Alhudah Team',
    date: '2026-05-20',
    readingMinutes: 4,
    body: [
      {
        type: 'p',
        text: 'Most people who want a closer relationship with the Quran do not lack sincerity. What they lack is a routine that survives an ordinary, busy week.',
      },
      {
        type: 'p',
        text: 'The instinct is to start big — a page a day, a juz a week, a finish-by date circled on the calendar. It works beautifully for about a week. Then a late night, a deadline, or a sick child arrives, the streak breaks, and the whole plan quietly collapses.',
      },
      { type: 'h2', text: 'Start smaller than feels worthwhile' },
      {
        type: 'p',
        text: 'Choose an amount you could complete on your worst day, not your best one. Five ayahs. Half a page. One short surah. It should feel almost too small to count — that is the point. A modest habit that survives every week is worth far more than an ambitious one that lasts a fortnight.',
      },
      {
        type: 'p',
        text: 'Then anchor it to something you already do without thinking: the minutes after Fajr, the quiet before sleep, the first part of a commute. A habit attached to an existing routine needs far less willpower to keep.',
      },
      {
        type: 'quote',
        text: 'فَاقْرَءُوا مَا تَيَسَّرَ مِنَ الْقُرْآنِ',
        translation: 'So recite what is easy of the Quran.',
        source: 'Surah Al-Muzzammil · 73:20',
      },
      {
        type: 'p',
        text: 'Even the command to recite is paired with ease. The goal was never strain.',
      },
      { type: 'h2', text: 'Keep it calm, not a performance' },
      {
        type: 'p',
        text: 'A habit you quietly dread will not last. Read at a pace that lets you actually notice the words. If you miss a day, skip the guilt entirely — open the Quran the next day and simply continue. There is no counter here keeping score against you.',
      },
      {
        type: 'p',
        text: 'The aim is not to finish the Quran by a particular date. It is to still be reading it, unhurried, a year from now — and the year after that.',
      },
    ],
  },
  {
    slug: 'reading-with-tafsir',
    title: 'Reading with tafsir, without getting lost',
    excerpt:
      'Tafsir opens the Quran’s context and depth — but it is easy to drown in it. Here is a lighter way to lean on it.',
    category: 'Study',
    author: 'The Alhudah Team',
    date: '2026-05-12',
    readingMinutes: 4,
    body: [
      {
        type: 'p',
        text: 'Tafsir is the scholarly explanation of the Quran: the circumstances a verse was revealed in, the meaning of its words to its first listeners, and how scholars across the centuries have understood it. It is one of the great inheritances of the tradition.',
      },
      {
        type: 'p',
        text: 'It is also easy to misuse. Open a full tafsir for every single ayah and a ten-minute reading becomes an hour of footnotes — interesting, but exhausting. Do that a few times and most people simply stop opening the Quran at all.',
      },
      { type: 'h2', text: 'Read first, then ask' },
      {
        type: 'p',
        text: 'Read the passage in translation at a natural pace. Notice which ayah made you pause — the one that confused you, or the one that quietly moved you. That is exactly where tafsir earns its place. Bring your question to it, rather than asking it to narrate every line.',
      },
      {
        type: 'p',
        text: 'In Alhudah, tafsir sits one tap away on each ayah and stays closed until you ask for it. The page remains calm; the depth is there the moment you want it.',
      },
      { type: 'h2', text: 'What tafsir answers well' },
      {
        type: 'p',
        text: 'A few questions it is especially good for: Why was this passage revealed? What did a particular word mean to the people who first heard it? How does this verse sit alongside others on the same theme? These are the questions that turn a line you read into a line you understand.',
      },
      {
        type: 'quote',
        text: 'كِتَابٌ أَنزَلْنَاهُ إِلَيْكَ مُبَارَكٌ لِّيَدَّبَّرُوا آيَاتِهِ',
        translation:
          'A blessed Book that We have revealed to you, that they might reflect upon its verses.',
        source: 'Surah Sad · 38:29',
      },
      {
        type: 'p',
        text: 'Tafsir is a companion to that reflection — never a replacement for it. Let it answer your questions, then return to the verse itself and sit with it.',
      },
    ],
  },
  {
    slug: 'a-calm-approach-to-memorization',
    title: 'A calm approach to memorization',
    excerpt:
      'Hifz rewards patience, not pressure. Small portions, honest repetition, and steady revision do the quiet work.',
    category: 'Memorization',
    author: 'The Alhudah Team',
    date: '2026-05-04',
    readingMinutes: 4,
    body: [
      {
        type: 'p',
        text: 'Memorizing the Quran can feel intimidating from the outside — but it has always been done the same patient, unglamorous way: one small portion at a time.',
      },
      { type: 'h2', text: 'Small portions, fully settled' },
      {
        type: 'p',
        text: 'Do not move on until the portion in front of you is genuinely secure. Three ayahs that you know cold are worth more than a full page that is shaky. Pace is not the measure here; certainty is.',
      },
      { type: 'h2', text: 'Repeat, hide, recall' },
      {
        type: 'p',
        text: 'The core loop is simple. Read the portion with the text in front of you, several times, until it feels familiar. Then hide it and try to recall it from memory. Then check. Each pass through that loop moves the words a little deeper.',
      },
      {
        type: 'p',
        text: 'Alhudah’s Memorize mode is built around exactly this rhythm — repeat, hide, and recall, paced entirely by you, with recitation audio so you can hear the portion as well as read it. Reciting aloud and listening engages more than the eyes alone, and it tends to stick better.',
      },
      { type: 'h2', text: 'Revision is the real work' },
      {
        type: 'p',
        text: 'New memorization is the exciting part. Revision is the part that actually keeps it. Give more time to revisiting old portions than to taking on new ones — what you do not return to, you slowly lose. A little revision every day is the whole secret.',
      },
      {
        type: 'quote',
        text: 'وَلَقَدْ يَسَّرْنَا الْقُرْآنَ لِلذِّكْرِ',
        translation: 'And We have certainly made the Quran easy to remember.',
        source: 'Surah Al-Qamar · 54:17',
      },
      {
        type: 'p',
        text: 'When it feels slow, return to that promise. The Quran was made easy to remember. Your part is simply to keep showing up for it.',
      },
    ],
  },
  {
    slug: 'the-quran-in-translation',
    title: 'The Quran in translation: a window, not a replacement',
    excerpt:
      'A translation lets you understand the Quran today — while the Arabic remains the Quran itself. Both matter, for different reasons.',
    category: 'Understanding',
    author: 'The Alhudah Team',
    date: '2026-04-25',
    readingMinutes: 3,
    body: [
      {
        type: 'p',
        text: 'Most readers meet the Quran first through a translation. That is a real gift: it means the meaning is open to you today, in a language you think in. It is also worth understanding a translation for what it actually is.',
      },
      { type: 'h2', text: 'A translation is an interpretation' },
      {
        type: 'p',
        text: 'Arabic carries layers that rarely survive the crossing into English — root meanings that echo other words, rhythm, and ambiguity that is sometimes held open on purpose. A translator has to choose one reading and commit to it. That is why two careful, faithful translations of the same ayah can read quite differently.',
      },
      {
        type: 'p',
        text: 'So a translation is best treated as a clear window onto the meaning — not as the Quran itself. The Quran is the Arabic. The translation is how you reach it for now.',
      },
      { type: 'h2', text: 'Read more than one' },
      {
        type: 'p',
        text: 'Comparing translations is genuinely useful. Where they agree, the meaning is settled; where they differ, you have found a verse with more depth than a single sentence can hold. Alhudah keeps English and Urdu alongside the Arabic for exactly this reason.',
      },
      { type: 'h2', text: 'Let it pull you toward the Arabic' },
      {
        type: 'p',
        text: 'Use translation fully and gratefully — and let it lead somewhere. Over time, start noticing the words that recur: Allah, rabb, rahmah, taqwa, hudā. Learn a handful at a time. Slowly the Arabic stops being a closed door, and the translation becomes what it was always meant to be — a way in.',
      },
    ],
  },
];

export function getPost(slug: string): BlogPost | undefined {
  return BLOG_POSTS.find((p) => p.slug === slug);
}

/** Human-readable date, e.g. "20 May 2026". */
export function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}
